import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    forwardRef,
    input,
    output,
    signal,
    ViewEncapsulation
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ToggleSwitchModule } from 'primeng/toggleswitch';

@Component({
    selector: 'fcp-toggle-switch',
    imports: [CommonModule, FormsModule, ToggleSwitchModule],
    styleUrl: './fcp-toggle-switch.component.less',
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FcpToggleSwitchComponent),
            multi: true
        }
    ],
    host: {
        '[attr.data-e2e-hook]': 'e2e()'
    },
    template: `
        <p-toggleswitch
            [disabled]="isDisabled()"
            [readonly]="readonly()"
            [trueValue]="trueValue()"
            [falseValue]="falseValue()"
            [ariaLabel]="ariaLabel()"
            [ariaLabelledBy]="ariaLabelledBy()"
            [autofocus]="autofocus()"
            [tabindex]="tabindex()"
            [inputId]="inputId()"
            [name]="name()"
            [ngModel]="model()"
            (onChange)="handleChange($event)"
        >
        </p-toggleswitch>
    `
})
export class FcpToggleSwitchComponent implements ControlValueAccessor {
    e2e = input<string | null>(null);
    disabled = input<boolean>(false);
    readonly = input<boolean>(false);
    trueValue = input<boolean | string | number>(true);
    falseValue = input<boolean | string | number>(false);
    ariaLabel = input<string | undefined>();
    ariaLabelledBy = input<string | undefined>();
    autofocus = input<boolean>(false);
    tabindex = input<number | undefined>();
    inputId = input<string | undefined>();
    name = input<string | undefined>();

    onChange = output<ToggleSwitchChangeEvent>();

    private _value = signal<boolean | string | number>(false);
    private _formDisabled = signal<boolean>(false);
    private _onChange = signal<(value: boolean | string | number) => void>(() => {});
    private _onTouched = signal<() => void>(() => {});

    model = computed(() => this._value());
    isDisabled = computed(() => this.disabled() || this.readonly() || this._formDisabled());

    writeValue(value: boolean | string | number | null | undefined): void {
        this._value.set(value ?? this.falseValue());
    }

    registerOnChange(fn: (value: boolean | string | number) => void): void {
        this._onChange.set(fn);
    }

    registerOnTouched(fn: () => void): void {
        this._onTouched.set(fn);
    }

    setDisabledState(isDisabled: boolean): void {
        this._formDisabled.set(isDisabled);
    }

    protected handleChange(event: ToggleSwitchChangeEvent): void {
        // Extract the actual value based on checked state and trueValue/falseValue
        const value = event.checked ? this.trueValue() : this.falseValue();

        this._value.set(value);
        this._onChange()(value);
        this.onChange.emit(event);
        this._onTouched()();
    }
}

export interface ToggleSwitchChangeEvent {
    originalEvent: Event;
    checked: boolean;
}
